import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "../../data/siteContent";

/**
 * Cinematic full-bleed quote section.
 * Uses one of the studio portraits as a background image with a strong
 * dark overlay; the portrait scrolls slower than the page (parallax).
 */
export const CinematicQuote = ({
  image = ASSETS.bookingPortrait,
  overline = "— On The Mission",
  quote = "I'm not here to make you feel good. I'm here to make you tell yourself the truth — once, and for the rest of your life.",
  attribution = "Manuel Santana",
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      ref={ref}
      className="relative h-[80vh] min-h-[520px] md:h-[90vh] md:min-h-[640px] w-full overflow-hidden bg-[#0a0a0a]"
      data-testid="cinematic-quote-section"
    >
      <motion.div className="absolute inset-0 w-full h-[120%] -top-[10%]" style={{ y }}>
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-[center_15%] select-none"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50" />
      </motion.div>

      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#C8102E]"
        >
          {overline}
        </motion.p>
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-8 max-w-4xl font-display text-white text-[clamp(32px,5vw,72px)] leading-[1.05] tracking-[-0.02em]"
        >
          "{quote}"
        </motion.blockquote>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 font-mono-ui text-[11px] tracking-[0.3em] uppercase text-white/70"
        >
          — {attribution}
        </motion.p>
      </div>
    </section>
  );
};

/**
 * Parallax wrapper for a hero portrait — slows it down as the user scrolls.
 */
export const ParallaxPortrait = ({ src, alt, className = "", strength = 80 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [strength, -strength]);

  return (
    <div ref={ref} className={`portrait-frame overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        draggable={false}
        style={{ y }}
        className="w-full h-[115%] object-cover object-[center_20%] select-none"
      />
    </div>
  );
};
